import { WalletReadyState } from '@solana/wallet-adapter-base'
import type { Adapter } from '@solana/wallet-adapter-base'

export interface SolanaConnectorProvider {
  id: string
  name: string
  /** Name the wallet registers under (Wallet Standard / wallet-adapter). */
  walletName: string
  /** Where to get the wallet when it is not installed; named wallets only. */
  installUrl?: string
}

/** The wallets offered by name even when not installed (FE-306). */
export const getSolanaConnectorProviders = (): SolanaConnectorProvider[] => [
  {
    id: 'phantom',
    name: 'Phantom',
    walletName: 'Phantom',
    installUrl: 'https://phantom.com/download'
  },
  {
    id: 'solflare',
    name: 'Solflare',
    walletName: 'Solflare',
    installUrl: 'https://solflare.com/download'
  },
  {
    id: 'backpack',
    name: 'Backpack',
    walletName: 'Backpack',
    installUrl: 'https://backpack.app/downloads'
  }
]

type AdapterEntry = { adapter: Adapter; readyState: WalletReadyState }

/**
 * Adapters that are not wallet apps a user picks here: Privy's embedded
 * wallet (connected automatically) and the WalletConnect bridge (its QR lives
 * in the Privy modal).
 */
const NOT_PICKABLE = new Set(['Privy', 'WalletConnect'])

export interface WalletAppOption {
  provider: SolanaConnectorProvider
  isInstalled: boolean
  /** The icon the installed wallet announces (Wallet Standard data URI). */
  icon?: string
}

/** The wallet-adapter entry of a provider; Wallet Standard wallets appear only once installed. */
export const findProviderWallet = (
  wallets: readonly AdapterEntry[],
  provider: SolanaConnectorProvider
) => wallets.find(entry => entry.adapter.name === provider.walletName)

export const isProviderInstalled = (
  wallets: readonly AdapterEntry[],
  provider: SolanaConnectorProvider
) =>
  findProviderWallet(wallets, provider)?.readyState ===
  WalletReadyState.Installed

const toProvider = (name: string): SolanaConnectorProvider => ({
  id: name.toLowerCase().replace(/\s+/g, '-'),
  name,
  walletName: name
})

/**
 * Every wallet app to offer: the named ones (installed or with a download
 * link), then any other installed Solana wallet the browser announces —
 * Jupiter, OKX, MetaMask, Bitget… — without code changes per wallet.
 */
export const getWalletAppOptions = (
  wallets: readonly AdapterEntry[]
): WalletAppOption[] => {
  const named = getSolanaConnectorProviders().map(provider => {
    const entry = findProviderWallet(wallets, provider)

    return {
      provider,
      isInstalled: entry?.readyState === WalletReadyState.Installed,
      icon: entry?.adapter.icon
    }
  })
  const namedWallets = new Set(named.map(option => option.provider.walletName))

  const others = wallets
    .filter(
      entry =>
        entry.readyState === WalletReadyState.Installed &&
        !namedWallets.has(entry.adapter.name) &&
        !NOT_PICKABLE.has(entry.adapter.name)
    )
    .map(entry => ({
      provider: toProvider(entry.adapter.name),
      isInstalled: true,
      icon: entry.adapter.icon
    }))

  return [...named, ...others]
}
